/**
 * Sezione Mapping identificatori (CPE/PURL) nel dettaglio prodotto.
 *
 * Funzionalità:
 *  - Lista mapping attivi con pulsante rimuovi (per chi ha PORTFOLIO_MANAGEMENT).
 *  - Form per aggiungere un nuovo mapping.
 *  - Import CSV con toggle dry-run: mostra il riepilogo prima di applicare.
 */

import { useState, type FormEvent } from 'react';
import { usePermissions } from '../../auth/index.js';
import { useMappings, useAddMapping, useRemoveMapping, useImportMappings } from './hooks.js';
import type { MappingInput, CsvImportSummary } from './types.js';
import styles from './Products.module.css';

/** Riepilogo dopo dry-run o import effettivo CSV. */
function CsvImportRiepilogo({ summary, dryRun }: { summary: CsvImportSummary; dryRun: boolean }) {
  return (
    <div className={styles.csvSummary} data-testid="csv-riepilogo">
      <p className={styles.csvSummaryTitle}>
        {dryRun ? 'Anteprima import (dry-run)' : 'Import completato'}
      </p>
      <div className={styles.csvSummaryRow}>
        <span>Creati: {summary.created}</span>
        <span>Saltati: {summary.skipped}</span>
      </div>
      {summary.parseErrors.length > 0 && (
        <div className={styles.csvSummaryError} data-testid="csv-parse-errors">
          Errori di parsing: {summary.parseErrors.join('; ')}
        </div>
      )}
      {summary.errors.length > 0 && (
        <div className={styles.csvSummaryError} data-testid="csv-import-errors">
          Errori import: {summary.errors.join('; ')}
        </div>
      )}
    </div>
  );
}

/** Sezione Mapping identificatori. */
export function MappingSection({ productId }: { productId: string }) {
  const { can } = usePermissions();
  const { data: mappings, isLoading, isError, error } = useMappings(productId);
  const addMapping = useAddMapping(productId);
  const removeMapping = useRemoveMapping(productId);
  const importMappings = useImportMappings();

  const puoGestire = can('PORTFOLIO_MANAGEMENT');

  // Stato form aggiunta mapping
  const [kind, setKind] = useState<'cpe' | 'purl'>('cpe');
  const [value, setValue] = useState('');

  // Stato import CSV
  const [csvText, setCsvText] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [lastSummary, setLastSummary] = useState<CsvImportSummary | null>(null);

  function handleAddMapping(e: FormEvent) {
    e.preventDefault();
    const input: MappingInput = { kind, value: value.trim() };
    addMapping.mutate(input, {
      onSuccess: () => setValue(''),
    });
  }

  function handleImport(e: FormEvent) {
    e.preventDefault();
    importMappings.mutate(
      { csv: csvText.trim(), dryRun },
      {
        onSuccess: (summary) => {
          setLastSummary(summary);
          if (!dryRun) setCsvText('');
        },
      },
    );
  }

  return (
    <div data-testid="sezione-mapping">
      {/* Lista mapping esistenti */}
      {isLoading && (
        <p className={styles.loadingMsg} data-testid="loading-mappings">
          Caricamento mapping…
        </p>
      )}
      {isError && (
        <p className={styles.errorMsg} data-testid="errore-mappings">
          {error instanceof Error ? error.message : 'Errore nel caricamento dei mapping'}
        </p>
      )}
      {!isLoading && !isError && mappings !== undefined && mappings.length === 0 && (
        <div className={styles.emptyState} data-testid="mappings-vuoti">
          Nessun mapping registrato.
        </div>
      )}
      {!isLoading && !isError && mappings !== undefined && mappings.length > 0 && (
        <table
          className={styles.table}
          data-testid="tabella-mappings"
          style={{ marginBottom: '1rem' }}
        >
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Valore</th>
              {puoGestire && <th>Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {mappings.map((m) => (
              <tr key={`${m.kind}-${m.value}`} data-testid={`mapping-row-${m.kind}-${m.value}`}>
                <td>
                  <span className={styles.badge}>{m.kind}</span>
                </td>
                <td style={{ wordBreak: 'break-all' }}>{m.value}</td>
                {puoGestire && (
                  <td>
                    <button
                      className={`${styles.btnDanger} ${styles.btnSmall}`}
                      onClick={() => {
                        if (window.confirm(`Rimuovere il mapping ${m.kind}: ${m.value}?`)) {
                          removeMapping.mutate({ kind: m.kind, value: m.value });
                        }
                      }}
                      disabled={removeMapping.isPending}
                      data-testid={`btn-rimuovi-mapping-${m.kind}-${m.value}`}
                    >
                      Rimuovi
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Form aggiunta mapping (solo con PORTFOLIO_MANAGEMENT) */}
      {puoGestire && (
        <div className={styles.formCard} data-testid="form-aggiungi-mapping">
          <p className={styles.formTitle}>Aggiungi mapping</p>
          <form onSubmit={handleAddMapping}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="mapping-kind">
                  Tipo *
                </label>
                <select
                  id="mapping-kind"
                  className={styles.formSelect}
                  value={kind}
                  onChange={(e) => setKind(e.target.value as 'cpe' | 'purl')}
                  data-testid="select-mapping-kind"
                >
                  <option value="cpe">CPE</option>
                  <option value="purl">PURL</option>
                </select>
              </div>
              <div className={styles.formGroup} style={{ flex: 3 }}>
                <label className={styles.formLabel} htmlFor="mapping-value">
                  Valore *
                </label>
                <input
                  id="mapping-value"
                  className={styles.formInput}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  required
                  placeholder="es. cpe:2.3:a:..."
                  data-testid="input-mapping-value"
                />
              </div>
            </div>
            <div className={styles.formActions}>
              <button
                type="submit"
                className={styles.btnPrimary}
                disabled={addMapping.isPending}
                data-testid="btn-aggiungi-mapping"
              >
                {addMapping.isPending ? 'Aggiunta…' : 'Aggiungi'}
              </button>
              {addMapping.isError && (
                <span className={styles.inlineMutError} data-testid="errore-aggiungi-mapping">
                  {addMapping.error instanceof Error
                    ? addMapping.error.message
                    : "Errore durante l'aggiunta"}
                </span>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Import CSV (solo con PORTFOLIO_MANAGEMENT) */}
      {puoGestire && (
        <div className={styles.formCard} data-testid="form-import-csv">
          <p className={styles.formTitle}>Import mapping da CSV</p>
          <p style={{ fontSize: '0.82rem', color: '#6c757d', margin: '0 0 0.75rem' }}>
            Formato CSV: <code>internalId,kind,value</code> (una riga per mapping)
          </p>
          <form onSubmit={handleImport}>
            <div className={styles.formGroup} style={{ marginBottom: '0.75rem' }}>
              <label className={styles.formLabel} htmlFor="csv-text">
                Contenuto CSV *
              </label>
              <textarea
                id="csv-text"
                className={styles.formTextarea}
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                required
                placeholder={'myapp-core,cpe,cpe:2.3:a:...\nmyapp-core,purl,pkg:maven/...'}
                rows={5}
                data-testid="textarea-csv"
              />
            </div>
            <div className={styles.formActions}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '0.88rem',
                }}
              >
                <input
                  type="checkbox"
                  checked={dryRun}
                  onChange={(e) => {
                    setDryRun(e.target.checked);
                    setLastSummary(null);
                  }}
                  data-testid="checkbox-dryrun"
                />
                Dry-run (anteprima senza applicare)
              </label>
              <button
                type="submit"
                className={styles.btnPrimary}
                disabled={importMappings.isPending}
                data-testid="btn-import-csv"
              >
                {importMappings.isPending ? 'Importazione…' : dryRun ? 'Anteprima' : 'Importa'}
              </button>
              {importMappings.isError && (
                <span className={styles.inlineMutError} data-testid="errore-import-csv">
                  {importMappings.error instanceof Error
                    ? importMappings.error.message
                    : "Errore durante l'import"}
                </span>
              )}
            </div>
          </form>
          {lastSummary && <CsvImportRiepilogo summary={lastSummary} dryRun={lastSummary.dryRun} />}
        </div>
      )}
    </div>
  );
}
